const microtaskSet = new Set();
export function addMicrotask(func) {
    if (typeof func !== 'function')
        return;
    if (!microtaskSet.size) {
        // delayed execution callback after updating store
        globalThis.queueMicrotask(() => {
            microtaskSet.forEach((func) => func());
            microtaskSet.clear();
        });
    }
    microtaskSet.delete(func);
    microtaskSet.add(func);
}
const microtaskStack = [];
function execMicrotaskStack() {
    for (let i = microtaskStack.length - 1; i >= 0; i--) {
        microtaskStack[i]();
    }
    microtaskStack.length = 0;
}
/**
 * 添加回调函数到微任务队列栈；
 * 先进后执行；
 */
export function addMicrotaskToStack(func) {
    if (!microtaskStack.length) {
        addMicrotask(execMicrotaskStack);
    }
    microtaskStack.push(func);
}
export function absoluteLocation(currentPath = '', relativePath = '') {
    const { pathname, search, hash } = new URL(relativePath, location.origin + currentPath);
    return pathname + search + hash;
}
// work on nodejs
export class LinkedList extends (globalThis.EventTarget || Object) {
    constructor() {
        super(...arguments);
        this.pool = new Map();
    }
    // 添加已存在的项目时会删除老的项目
    add(value) {
        const item = { value };
        if (!this.firstItem) {
            this.dispatchEvent(new CustomEvent('start'));
            this.firstItem = item;
        }
        if (this.lastItem) {
            this.lastItem.next = item;
            item.prev = this.lastItem;
        }
        this.lastItem = item;
        const existItem = this.pool.get(value);
        if (existItem) {
            if (existItem.prev) {
                existItem.prev.next = existItem.next;
            }
            else {
                this.firstItem = existItem.next;
            }
            if (existItem.next) {
                existItem.next.prev = existItem.prev;
            }
        }
        this.pool.set(value, item);
    }
    get() {
        const firstItem = this.firstItem;
        if (!firstItem)
            return;
        this.pool.delete(firstItem.value);
        this.firstItem = firstItem.next;
        if (!this.firstItem) {
            this.lastItem = undefined;
            this.dispatchEvent(new CustomEvent('end'));
        }
        return firstItem.value;
    }
}
export class PropProxyMap extends WeakMap {
    get(ele) {
        let proxy = super.get(ele);
        if (!proxy) {
            proxy = {};
            this.set(ele, proxy);
        }
        return proxy;
    }
}
export class QueryString extends URLSearchParams {
    constructor(param) {
        if (param instanceof QueryString) {
            return param;
        }
        super(param);
    }
    concat(param) {
        let query;
        if (typeof param === 'string') {
            query = Object.fromEntries(new URLSearchParams(param).entries());
        }
        else {
            query = param;
        }
        Object.keys(query).forEach((key) => {
            this.append(key, query[key]);
        });
    }
    toString() {
        const string = super.toString();
        return string ? `?${string}` : '';
    }
    toJSON() {
        return this.toString();
    }
}
/**
 * 写纯文本，仅用于 IDE HTML 高亮
 *
 * 不同于 lit-html 的 `html` 模版函数，写 attribute 时必须手动写引号并自行进行 html 编码
 */
export function raw(arr, ...args) {
    return arr.reduce((prev, current, index) => { var _a; return prev + ((_a = args[index - 1]) !== null && _a !== void 0 ? _a : '') + current; });
}
// 写 css 文本，在 CSSStyleSheet 中使用
export function css(arr, ...args) {
    return raw(arr, ...args);
}
export const SheetToken = Symbol('sheet token');
/**
 * !!! 目前只有 Chrome/FirefoxNightly 支持
 * https://bugzilla.mozilla.org/show_bug.cgi?id=1520690
 *
 * 创建 style sheet 用于 `adoptedStyleSheets`，不支持样式更新
 * @param rules string | Record<string, string> 不能动态更新的 css
 * @param mediaQuery string 媒体查询 StyleSheet.media.mediaText
 */
export function createCSSSheet(rules, mediaQuery = '') {
    const styleSheet = new CSSStyleSheet();
    const sheet = {};
    styleSheet.media.mediaText = mediaQuery;
    let style = '';
    if (typeof rules === 'string') {
        style = rules;
    }
    else {
        Object.keys(rules).forEach((key) => {
            sheet[key] = rules[key].type === 'tag' ? key : `${key}-${rules[key].key}`;
            style += rules[key].str.replace(/&/g, sheet[key]);
        });
    }
    styleSheet.replaceSync(style);
    sheet[SheetToken] = styleSheet;
    return sheet;
}
export function randomStr(number = 5, origin = '0123456789abcdefghijklmnopqrstuvwxyz') {
    const len = origin.length;
    let str = '';
    for (let i = 0; i < number; i++) {
        str += origin[Math.floor(Math.random() * len)];
    }
    return str;
}
// 只支持一层嵌套
// https://drafts.csswg.org/css-nesting-1/
function flatStyled(style, type) {
    const subStyle = [];
    let str = `& {${style.replace(new RegExp('&.*{((.|\n)*)}', 'g'), function (substr) {
        subStyle.push(substr);
        return '';
    })}}` + subStyle.join('');
    if (type !== 'tag')
        str = str.replace(/&/g, type === 'class' ? '.&' : '#&');
    return { str, key: randomStr(), type };
}
// 写 css 文本，在 CSSStyleSheet 中使用，使用 styed-components 高亮
// 暂时不支持 `at` 规则
//
// createCSSSheet({
//   red: styled.class`
//     background: red;
//     &:hover {
//       background: blue;
//     }
//   `,
// });
export const styled = {
    class: (arr, ...args) => {
        const style = raw(arr, ...args);
        return flatStyled(style, 'class');
    },
    id: (arr, ...args) => {
        const style = raw(arr, ...args);
        return flatStyled(style, 'id');
    },
    tag: (arr, ...args) => {
        const style = raw(arr, ...args);
        return flatStyled(style, 'tag');
    },
};
export function camelToKebabCase(str) {
    return str.replace(/[A-Z]/g, ($1) => '-' + $1.toLowerCase());
}
export function kebabToCamelCase(str) {
    return str.replace(/-(.)/g, (_substr, $1) => $1.toUpperCase());
}
export function cleanObject(o) {
    Object.keys(o).forEach((key) => {
        const k = key;
        delete o[k];
    });
    return o;
}
export class GemError extends Error {
    constructor(msg) {
        super(msg);
        this.message = `gem: ${this.message}`;
    }
}
export function isArrayChange(newValues, oldValues) {
    const length = newValues.length;
    for (let i = 0; i < length; i++) {
        if (newValues[i] !== oldValues[i])
            return true;
    }
    return false;
}
export function styleMap(obj) {
    let styleString = '';
    for (const key in obj) {
        styleString += `${camelToKebabCase(key)}:${obj[key]};`;
    }
    return styleString;
}
export function classMap(obj) {
    let classList = ' ';
    for (const className in obj) {
        if (obj[className]) {
            classList += `${className} `;
        }
    }
    return classList;
}
//# sourceMappingURL=utils.js.map